'use client';

var _CheckmarkCircle;
import { useEffect, useRef } from 'react';
import { CheckmarkCircle } from '@crehana/compass.icons.fill/v2/checkmark-circle';
import { cn } from "../utils";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * A single scrollable column used inside the columns variant picker.
 * Renders a listbox of items (hours, minutes, or a.m/p.m options).
 *
 * This component is mounted/unmounted with the Popper — Radix unmounts
 * PopperContent on close by default, so all local state (refs) resets
 * automatically each time the popover opens.
 */
export var TimePickerColumn = function TimePickerColumn(_ref) {
  var items = _ref.items,
    selectedValue = _ref.selectedValue,
    onSelect = _ref.onSelect,
    label = _ref.label;
  var listRef = useRef(null);
  var itemRefs = useRef(new Map());

  // Capture the value at mount time so the scroll-to-center effect only runs once
  // with the initial value — not on subsequent user clicks.
  var initialValueRef = useRef(selectedValue);

  // Scroll the initially selected item to the vertical center of the column.
  // This only fires on mount (empty deps) so clicking a new item never causes
  // an unwanted scroll jump. Without this guard, selecting an item near the
  // bottom of the visible area would re-center the column and shift the mouse
  // onto a different item — a bug reported during development.
  useEffect(function () {
    if (!initialValueRef.current) {
      return;
    }
    var selectedEl = itemRefs.current.get(initialValueRef.current);
    if (selectedEl && listRef.current) {
      var listRect = listRef.current.getBoundingClientRect();
      var itemRect = selectedEl.getBoundingClientRect();

      // Position the item at the vertical midpoint of the scrollable container.
      // offsetTop is relative to the column's scroll container, not the viewport.
      var scrollTop = selectedEl.offsetTop - listRef.current.offsetTop - listRect.height / 2 + itemRect.height / 2;
      listRef.current.scrollTop = Math.max(0, scrollTop);
    }
  }, []);
  return /*#__PURE__*/_jsx("div", {
    ref: listRef,
    role: "listbox",
    "aria-label": label,
    className: "tw-flex tw-flex-col tw-overflow-y-auto tw-h-24 tw-w-10 tw-shrink-0 tw-scrollbar-thin",
    children: items.map(function (item) {
      var isSelected = item === selectedValue;
      return /*#__PURE__*/_jsxs("button", {
        ref: function ref(el) {
          if (el) {
            itemRefs.current.set(item, el);
          } else {
            itemRefs.current["delete"](item);
          }
        },
        role: "option",
        "aria-selected": isSelected,
        type: "button",
        className: cn(
        // Fixed height + shrink-0 prevents items from collapsing when the column overflows
        'tw-w-10.4 tw-h-4.8 tw-px-2.4 tw-py-1.2 tw-flex tw-items-center tw-cursor-pointer tw-outline-none tw-shrink-0', 'tw-cds-font-body-02', 'hover:tw-cds-bg-layer-02', 'focus-visible:tw-ring-2 focus-visible:tw-ring-primary-light-500',
        // Selected: background highlight + gap for the checkmark icon
        isSelected && 'tw-cds-bg-layer-02 tw-gap-1.2'),
        onClick: function onClick() {
          return onSelect(item);
        },
        children: [/*#__PURE__*/_jsx("span", {
          children: item
        }), isSelected && (_CheckmarkCircle || (_CheckmarkCircle = /*#__PURE__*/_jsx(CheckmarkCircle, {
          size: 20
          // Uses CSS variable directly because compass.icons doesn't accept
          // Tailwind token classes for fill color — only inline color values.
          ,
          className: "tw-text-[var(--cds-icon-color-success)] tw-shrink-0"
        })))]
      }, item);
    })
  });
};